import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  RETENTION_CV_A_RATTACHER_JOURS,
  type CompteursDepotsCv,
  type DepotCvDetail,
  type ReponseDepotCv,
  type VueDepotsCv,
} from '@rh/shared';
import { appel } from '../api';
import { lireCompteursCv } from '../compteurs-cv';
import { CarteDepotCv, type NoticeCv } from '../composants/CarteDepotCv';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { ZoneDepotCv } from '../composants/ZoneDepotCv';

/** Relecture des seuls compteurs pendant une analyse. */
export const INTERVALLE_COMPTEURS_MS = 4000;
/** Au-delà (5 minutes sans changement), la page cesse de relire et le dit. */
export const RELECTURES_COMPTEURS_MAX = 75;

const ONGLETS: Array<{ vue: VueDepotsCv; libelle: string }> = [
  { vue: 'attente', libelle: 'En attente' },
  { vue: 'rattaches', libelle: 'Rattachés récents' },
  { vue: 'ecartes', libelle: 'Écartés' },
];

/** Écran « CV à rattacher » (docs/UI.md), rôle rh. */
export function CvARattacher({ intervalleMs = INTERVALLE_COMPTEURS_MS }: { intervalleMs?: number }) {
  const [vue, setVue] = useState<VueDepotsCv>('attente');
  const [depots, setDepots] = useState<DepotCvDetail[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [compteurs, setCompteurs] = useState<CompteursDepotsCv | null>(null);
  const [lecture, setLecture] = useState(0);
  const [notice, setNotice] = useState<NoticeCv | null>(null);
  const [suiviLong, setSuiviLong] = useState(false);
  const precedents = useRef<CompteursDepotsCv | null>(null);

  // Liste de l'onglet : au changement d'onglet, après une action, à la fin d'une série
  // d'envois et quand les compteurs bougent — jamais à chaque fichier envoyé.
  useEffect(() => {
    let vivant = true;
    appel<DepotCvDetail[]>(`/cv-a-rattacher?vue=${vue}`)
      .then((liste) => {
        if (!vivant) return;
        setDepots(liste);
        setErreur(null);
      })
      .catch((e: Error) => vivant && setErreur(e.message));
    return () => {
      vivant = false;
    };
  }, [vue, lecture]);

  /** Nouveaux compteurs ; avec `relireSiChange`, la liste est relue s'ils ont bougé. */
  const appliquer = useCallback((nouveaux: CompteursDepotsCv, relireSiChange: boolean) => {
    const avant = precedents.current;
    precedents.current = nouveaux;
    setCompteurs(nouveaux);
    if (relireSiChange && avant && (avant.analyse !== nouveaux.analyse || avant.aRattacher !== nouveaux.aRattacher)) {
      setLecture((n) => n + 1);
    }
  }, []);

  const rafraichir = useCallback(() => {
    setLecture((n) => n + 1);
    lireCompteursCv()
      .then((c) => appliquer(c, false))
      .catch(() => undefined);
  }, [appliquer]);

  useEffect(() => {
    lireCompteursCv()
      .then((c) => appliquer(c, false))
      .catch(() => undefined);
  }, [appliquer]);

  // Retour sur l'onglet du navigateur : compteurs relus, liste relue s'ils ont bougé.
  useEffect(() => {
    const surRetour = () => {
      if (document.visibilityState !== 'visible') return;
      lireCompteursCv()
        .then((c) => appliquer(c, true))
        .catch(() => undefined);
    };
    document.addEventListener('visibilitychange', surRetour);
    return () => document.removeEventListener('visibilitychange', surRetour);
  }, [appliquer]);

  // Pendant l'analyse, relecture sobre des seuls compteurs ; minuteur annulé au démontage.
  const enAnalyse = (compteurs?.analyse ?? 0) > 0;
  useEffect(() => {
    if (!enAnalyse) return;
    let vivant = true;
    let minuteur: ReturnType<typeof setTimeout> | undefined;
    let relectures = 0;
    setSuiviLong(false);
    const planifier = () => {
      minuteur = setTimeout(() => {
        // Onglet masqué : aucune requête, on repassera.
        if (document.visibilityState === 'hidden') {
          planifier();
          return;
        }
        relectures += 1;
        lireCompteursCv().then(
          (c) => {
            if (!vivant) return;
            appliquer(c, true);
            if (c.analyse === 0) return;
            if (relectures < RELECTURES_COMPTEURS_MAX) planifier();
            else setSuiviLong(true);
          },
          () => {
            if (vivant && relectures < RELECTURES_COMPTEURS_MAX) planifier();
          },
        );
      }, intervalleMs);
    };
    planifier();
    return () => {
      vivant = false;
      clearTimeout(minuteur);
    };
  }, [enAnalyse, lecture, intervalleMs, appliquer]);

  const surReponse = useCallback(
    (reponse: ReponseDepotCv) => {
      const depot = reponse.depot;
      if (!depot || vue !== 'attente' || (depot.statut !== 'analyse' && depot.statut !== 'a_rattacher')) return;
      setDepots((liste) => (liste && !liste.some((d) => d.id === depot.id) ? [depot, ...liste] : liste));
    },
    [vue],
  );

  const surFait = useCallback(
    (nouvelle: NoticeCv) => {
      setNotice(nouvelle);
      rafraichir();
    },
    [rafraichir],
  );

  function changerVue(suivante: VueDepotsCv): void {
    if (suivante === vue) return;
    setVue(suivante);
    setDepots(null);
    setErreur(null);
  }

  const jours = compteurs?.retentionJours ?? RETENTION_CV_A_RATTACHER_JOURS;
  const aConfirmer = depots?.filter((d) => d.statut === 'a_rattacher') ?? [];
  const enLecture = depots?.filter((d) => d.statut === 'analyse') ?? [];

  return (
    <section className="cv-a-rattacher">
      <div className="entete-annonce">
        <h2>CV à rattacher</h2>
      </div>
      <p className="mention">
        Téléchargez le CV depuis Tunisie Travail (lien « Voir sur Tunisie Travail » de la fiche), puis déposez-le
        ici. L’application le lit et ne le rattache seule que si son téléphone ou son e-mail désigne une seule fiche
        dont le prénom et le nom figurent en tête du CV ; sinon il attend votre confirmation. Un CV non rattaché est
        effacé {jours} jours après son dépôt, sans préavis.
      </p>

      <ZoneDepotCv onReponse={surReponse} onFileVide={rafraichir} />

      {notice && (
        <p className={notice.alerte ? 'notice-cv notice-cv--alerte' : 'notice-cv'} role="status">
          {notice.texte}
          {notice.candidatureId && (
            <>
              {' '}
              <Link to={`/candidatures/${notice.candidatureId}`}>Ouvrir la fiche</Link>
            </>
          )}
        </p>
      )}

      <div className="filtres" role="group" aria-label="État des CV déposés">
        {ONGLETS.map((onglet) => (
          <button
            key={onglet.vue}
            type="button"
            className={onglet.vue === vue ? 'actif' : ''}
            aria-pressed={onglet.vue === vue}
            onClick={() => changerVue(onglet.vue)}
          >
            {onglet.libelle}
            {onglet.vue === 'attente' && compteurs ? ` (${compteurs.analyse + compteurs.aRattacher})` : ''}
          </button>
        ))}
      </div>

      {suiviLong && enAnalyse && (
        <p className="signal">Lecture toujours en cours : revenez dans quelques minutes.</p>
      )}
      {erreur && <Erreur message={erreur} />}
      {!depots && !erreur && <Chargement quoi="des CV déposés" />}

      {depots && vue === 'attente' && (
        <>
          <h3>À confirmer ({aConfirmer.length})</h3>
          {aConfirmer.length === 0 ? (
            <Vide enfants="Aucun CV à confirmer." />
          ) : (
            <ul className="depots-cv">
              {aConfirmer.map((depot) => (
                <CarteDepotCv key={depot.id} depot={depot} onFait={surFait} />
              ))}
            </ul>
          )}
          {enLecture.length > 0 && (
            <>
              <h3>Lecture en cours ({enLecture.length})</h3>
              <ul className="depots-cv">
                {enLecture.map((depot) => (
                  <CarteDepotCv key={depot.id} depot={depot} onFait={surFait} />
                ))}
              </ul>
            </>
          )}
        </>
      )}

      {depots &&
        vue !== 'attente' &&
        (depots.length === 0 ? (
          <Vide enfants={vue === 'rattaches' ? 'Aucun CV rattaché récemment.' : 'Aucun CV écarté ni effacé.'} />
        ) : (
          <ul className="depots-cv">
            {depots.map((depot) => (
              <CarteDepotCv key={depot.id} depot={depot} onFait={surFait} />
            ))}
          </ul>
        ))}
    </section>
  );
}
